/**
 * Login Component
 * Handles user authentication (email/password and OAuth)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Mail, Lock, User, Github, Chrome, Loader2 } from 'lucide-react';

const Login = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, signInWithOAuth, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated (prevents showing login page to logged-in users)
  useEffect(() => {
    if (isAuthenticated) {
      console.log('User already authenticated, redirecting to app...');
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Check Supabase configuration on mount
  useEffect(() => {
    if (!supabase) {
      console.error('Supabase client is null - check environment variables');
      setError('Configuration error: Supabase is not configured. Please check your environment variables.');
      return;
    }
    console.log('Supabase client initialized successfully');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify Supabase is available before attempting auth
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      if (isSignUp) {
        console.log('Attempting sign up...');
        const result = await signUp(email, password, fullName);
        console.log('Sign up result:', result);
        
        // Check if email confirmation is required
        if (result.user && !result.session) {
          setError('Please check your email to confirm your account before signing in.');
          setLoading(false);
          return;
        }
        // Navigation will be handled by the isAuthenticated useEffect
      } else {
        console.log('Attempting sign in...');
        const result = await signIn(email, password);
        console.log('Sign in result:', result);
        // Navigation will be handled by the isAuthenticated useEffect
      }
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      setLoading(false);
      // Provide more helpful error messages
      if (err.message?.includes('fetch') || err.message?.includes('network') || err.message?.includes('Failed to fetch')) {
        setError('Network error: Unable to connect to Supabase. This might mean: 1) The project is still paused (check Supabase dashboard), 2) Network connectivity issues, or 3) The dev server needs a restart. Please check the browser console for details.');
      } else if (err.message?.includes('Supabase not configured')) {
        setError('Configuration error: Supabase credentials are missing. Please check your environment variables.');
      } else if (err.message?.includes('Invalid login') || err.message?.includes('Invalid credentials') || err.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    }
  };

  const handleKeyDown = (e) => {
    // Submit form when Enter is pressed
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      const form = e.target.closest('form');
      if (form) {
        form.requestSubmit();
      }
    }
  };

  const handleOAuth = async (provider) => {
    setError('');
    setLoading(true);

    try {
      await signInWithOAuth(provider);
      // OAuth redirects, so we don't navigate here
    } catch (err) {
      console.error('OAuth error:', err);
      if (err.message?.includes('fetch') || err.message?.includes('network')) {
        setError(`Network error: Please check your internet connection. If you're a developer, make sure Supabase credentials are configured.`);
      } else if (err.message?.includes('Supabase not configured')) {
        setError('Configuration error: Supabase credentials are missing. Please check your environment variables.');
      } else {
        setError(err.message || `Failed to sign in with ${provider}. Please try again.`);
      }
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.png" alt="GONGFU" className="login-logo-image" />
          <h1 className="login-title">gongfu</h1>
          <p>Product Management Simulation Platform</p>
        </div>

        <div className="login-tabs">
          <button
            className={`tab ${!isSignUp ? 'active' : ''}`}
            onClick={() => setIsSignUp(false)}
          >
            Sign In
          </button>
          <button
            className={`tab ${isSignUp ? 'active' : ''}`}
            onClick={() => setIsSignUp(true)}
          >
            Sign Up
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="fullName">
                <User size={18} />
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your full name"
                required
                disabled={loading}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">
              <Mail size={18} />
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your email"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <Lock size={18} />
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your password"
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="spinner" />
                {isSignUp ? 'Creating Account...' : 'Signing In...'}
              </>
            ) : (
              isSignUp ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        <div className="oauth-divider">
          <span>Or continue with</span>
        </div>

        <div className="oauth-buttons">
          <button
            type="button"
            className="oauth-button"
            onClick={() => handleOAuth('google')}
            disabled={loading}
          >
            <Chrome size={20} />
            Google
          </button>
          <button
            type="button"
            className="oauth-button"
            onClick={() => handleOAuth('github')}
            disabled={loading}
          >
            <Github size={20} />
            GitHub
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;



